import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mainSiteUrl } from '@/mocks/site';

type FooterLink = {
  label: string;
  target: string;
};

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const resources: FooterLink[] = [
    { label: t('nav_spreadsheet'), target: 'spreadsheet' },
    { label: t('nav_guides'), target: 'guides' },
    { label: t('nav_reviews'), target: 'guides' },
    { label: t('nav_shipping'), target: 'guides' },
    { label: t('nav_coupon'), target: 'guides' },
    { label: t('nav_warehouse'), target: 'guides' },
    { label: t('nav_safety'), target: 'safety' },
  ];

  const company: FooterLink[] = [
    { label: t('footer_about'), target: 'spreadsheet' },
    { label: t('footer_contact'), target: 'faq' },
    { label: t('footer_editorial'), target: 'spreadsheet' },
    { label: t('footer_updates'), target: 'guides' },
  ];

  function goToSection(target: string) {
    const scrollTo = () => {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (location.pathname === '/') {
      scrollTo();
    } else {
      navigate('/');
      setTimeout(scrollTo, 150);
    }
  }

  return (
    <footer className="bg-background-950 text-background-200">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 font-heading text-xl font-bold text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-white">
                <i className="ri-table-line text-base"></i>
              </span>
              {t('brand')}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-background-400">{t('footer_tagline')}</p>
          </div>

          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide text-white">{t('footer_resources')}</h4>
            <ul className="mt-4 space-y-2.5">
              {resources.map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => goToSection(item.target)}
                    className="cursor-pointer text-left text-sm text-background-400 transition-colors hover:text-white"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide text-white">{t('footer_company')}</h4>
            <ul className="mt-4 space-y-2.5">
              {company.map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => goToSection(item.target)}
                    className="cursor-pointer text-left text-sm text-background-400 transition-colors hover:text-white"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold uppercase tracking-wide text-white">{t('footer_links')}</h4>
            <ul className="mt-4 space-y-2.5">
              <li>
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-background-400 transition-colors hover:text-white">
                  {t('explore_main')}
                </a>
              </li>
              <li>
                <Link to="/usfans-legit" className="text-sm text-background-400 transition-colors hover:text-white">
                  {t('nav_safety')}
                </Link>
              </li>
              <li>
                <button
                  onClick={() => goToSection('safety')}
                  className="cursor-pointer text-left text-sm text-background-400 transition-colors hover:text-white"
                >
                  {t('footer_disclaimer')}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-background-800 pt-6 sm:flex-row">
          <p className="text-xs text-background-500">
            © {new Date().getFullYear()} {t('brand')}. {t('footer_rights')}
          </p>
          <p className="max-w-md text-center text-xs text-background-500 sm:text-right">{t('independent_note')}</p>
        </div>
      </div>
    </footer>
  );
}